import { useCallback } from 'react';

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { CrmObjectAvatar } from '../../components/CrmObjectAvatar';
import { TextValueLabel } from '../../components/properties/labels/TextValueLabel';
import { useOpenSlideout } from '../../hooks/useOpenSlideout';
import { getDisplayName } from '../../utils/getDisplayName';
import type { AssociationChange } from '../buildProjectedObject';

const CARD_BASE =
  'flex items-center gap-3 rounded-xl border shadow-xs p-3 transition-colors cursor-pointer w-full text-left';
const CARD_DEFAULT =
  'border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] hover:bg-[var(--color-bg-secondary)]';
/**
 * Added counterpart of `CARD_DEFAULT` — the card's OWN border and fill carry
 * the success tint (same language as the `added` entity badge), so a
 * projected association renders one outline, not a ring stacked on top of
 * the card's border.
 */
const CARD_ADDED =
  'border-utility-success-200 bg-utility-success-50 hover:bg-utility-success-100';

const CHANGE_MARKER_LABEL: Record<AssociationChange, string> = {
  added: 'Will be added',
  removed: 'Will be removed',
};

interface ContactCardProps {
  contact: CrmObject;
  onClick?: () => void;
  /**
   * Set when a proposal preview adds or removes this contact's association
   * with the record being viewed. The card styles itself accordingly.
   */
  changeMarker?: AssociationChange;
}

export function ContactCard({
  contact,
  onClick,
  changeMarker,
}: ContactCardProps) {
  const openSlideout = useOpenSlideout();
  const jobTitle = getPropertyValue<string>(contact.properties?.ahoy_job_title);

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      openSlideout(contact.entity_id);
    }
  }, [onClick, openSlideout, contact.entity_id]);

  return (
    <button
      type='button'
      onClick={handleClick}
      className={cx(
        CARD_BASE,
        changeMarker === 'added' ? CARD_ADDED : CARD_DEFAULT,
        changeMarker === 'removed' && 'opacity-70'
      )}
      data-change-marker={changeMarker}
    >
      {changeMarker && (
        <span className='sr-only'>{CHANGE_MARKER_LABEL[changeMarker]}</span>
      )}
      <CrmObjectAvatar object={contact} size='md' />
      <div className='flex flex-col min-w-0 flex-1'>
        <span className='text-xs md:text-sm font-medium text-[var(--color-text-primary)] truncate'>
          {getDisplayName(contact)}
        </span>
        {jobTitle && (
          <TextValueLabel
            value={jobTitle}
            orgId={contact.org_id}
            fontSize='xs'
            className='md:text-sm text-[var(--color-text-secondary)]'
            truncate
          />
        )}
      </div>
    </button>
  );
}
